import type { ChainConfig } from "@/domains/core/types"
import type { IGaugeRepository } from "@/domains/gauge"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { MinimalPoolApiData } from "@/domains/minimal-pool/types"
import type { TokenRepository } from "@/domains/tokens"
import type { WalletConnectionStore } from "@/domains/wallet"
import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"
import { MinimalPoolApiTransport } from "@/domains/minimal-pool/minimal-pool-api-transport"
import { MinimalPoolOnChainTransport } from "@/domains/minimal-pool/minimal-pool-on-chain-transport"
import { MinimalPoolOrchestrator } from "@/domains/minimal-pool/minimal-pool-orchestrator"
import { MinimalPoolRepository } from "@/domains/minimal-pool/minimal-pool-repository"
import { buildMinimalPoolBanSet } from "@/domains/minimal-pool/utils/build-minimal-pool-ban-set"
import { DefaultInfoPairValues } from "@/domains/on-chain/scalens/frontend-default-info-pairs"
import { ScalensUserManageMinimalPoolInfo } from "@/domains/on-chain/scalens/frontend-user-manage-minimal-pool"

export class MinimalPoolOrchestratorFactory {
  /**
   * Create a fully configured ClassicPoolOrchestrator with its OrchestratedQueries
   */
  static create(
    minimalPoolApiTransport: MinimalPoolApiTransport,
    minimalPoolRepository: MinimalPoolRepository,
    tokenRepository: TokenRepository,
    walletStore: WalletConnectionStore,
    orchestratedQueryStore: OrchestratedQueryStore,
    minimalPoolOnChainTransport: MinimalPoolOnChainTransport,
    routerAddress: LcAddress,
    eventBus: IEventBus,
    gaugeRepository: IGaugeRepository | undefined,
    bannedPairs: ChainConfig["bannedPairs"],
  ): MinimalPoolOrchestrator {
    const bannedPairAddresses = buildMinimalPoolBanSet(bannedPairs)
    // 1. Create orchestrator with all dependencies passed in
    const orchestrator = new MinimalPoolOrchestrator(
      minimalPoolRepository,
      minimalPoolApiTransport,
      tokenRepository,
      routerAddress,
      eventBus,
      gaugeRepository,
      bannedPairAddresses,
    )

    // 2. Create pools sync query
    const loadPoolsQuery = new OrchestratedQuery<Array<MinimalPoolApiData>>(
      "Minimal Pools Sync",
      (query) => orchestrator.onLoadPoolsTrigger(query),
      60_000,
      orchestrator.onLoadPoolsQuerySuccess.bind(orchestrator),
      orchestrator.onLoadPoolsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 3. Create info pairs sync query
    const infoPairsQuery = new OrchestratedQuery<Array<DefaultInfoPairValues>>(
      "Minimal Pool Info Pairs Sync",
      (query) => orchestrator.onLoadInfoPairsTrigger(query, minimalPoolOnChainTransport),
      30_000, // 30 seconds interval
      orchestrator.onLoadInfoPairsQuerySuccess.bind(orchestrator),
      orchestrator.onLoadInfoPairsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
      undefined, // rateLimit
      { min: 1, max: 200, default: 70 }, // chunkSizeBounds
    )

    const userInfoPairsQuery = new OrchestratedQuery<Array<any>>(
      "Minimal Pool User Info Pairs Sync",
      (query) => orchestrator.onLoadUserInfoPairsTrigger(query, minimalPoolOnChainTransport, walletStore),
      30_000, // 30 seconds interval
      orchestrator.onLoadUserInfoPairsQuerySuccess.bind(orchestrator),
      orchestrator.onLoadUserInfoPairsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
      undefined, // rateLimit
      { min: 1, max: 500, default: 100 }, // chunkSizeBounds
    )

    // 4. Create user manage minimal pool info query (page-controlled)
    const userManageMinimalPoolInfoQuery = new OrchestratedQuery<ScalensUserManageMinimalPoolInfo | undefined>(
      "User Manage Minimal Pool Info",
      (query) => orchestrator.onLoadUserManagePoolTrigger(query, minimalPoolOnChainTransport, walletStore),
      60_000, // 10 seconds interval (can be changed by page)
      orchestrator.onLoadUserManagePoolQuerySuccess.bind(orchestrator),
      orchestrator.onLoadUserManagePoolQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 5. Initialize orchestrator with ALL queries
    orchestrator.initialize(loadPoolsQuery, infoPairsQuery, userInfoPairsQuery, userManageMinimalPoolInfoQuery)

    // 6. Start automatic syncs (userManageMinimalPoolInfoQuery is started by pages)
    loadPoolsQuery.start()
    // infoPairsQuery.start()

    return orchestrator
  }
}
